import React, { useState } from "react";
import type { TicketMatch, EntityTypeConfig } from "../api/types";

/** Spiegazione testuale dei metodi di matching */
const METHOD_EXPLANATION: Record<string, string> = {
  exact: "Corrispondenza esatta con i dati nell'email.",
  EXACT: "Corrispondenza esatta con i dati nell'email.",
  learned: "Appreso da correzioni precedenti degli operatori.",
  LEARNED: "Appreso da correzioni precedenti degli operatori.",
  fuzzy: "Trovato per similarità testuale — potrebbe non essere preciso.",
  FUZZY: "Trovato per similarità testuale — potrebbe non essere preciso.",
  custom_rule: "Assegnato tramite regola automatica (es. impianto unico nell'edificio).",
  CUSTOM_RULE: "Assegnato tramite regola automatica (es. impianto unico nell'edificio).",
  vector: "Trovato con ricerca semantica — ultimo tentativo, potenziale imprecisione.",
  VECTOR: "Trovato con ricerca semantica — ultimo tentativo, potenziale imprecisione.",
  content_match: "Identificato analizzando parole chiave nel testo dell'email.",
  content_match_impianto: "Identificato analizzando parole chiave nel testo dell'email.",
  fk_lookup: "Derivato automaticamente dalla relazione con un'altra entità.",
  impianto_unico: "L'edificio ha un solo impianto, assegnato automaticamente.",
  filter_by_chiamante_edifici: "Trovato tramite gli edifici associati al chiamante.",
  none: "Nessun risultato trovato nell'anagrafica.",
};

const METHOD_SHORT: Record<string, string> = {
  exact: "Match esatto", EXACT: "Match esatto",
  learned: "Appreso", LEARNED: "Appreso",
  fuzzy: "Similarità", FUZZY: "Similarità",
  custom_rule: "Regola auto", CUSTOM_RULE: "Regola auto",
  vector: "Semantica", VECTOR: "Semantica",
  content_match: "Analisi contenuto", content_match_impianto: "Analisi contenuto",
  fk_lookup: "Derivato FK", impianto_unico: "Impianto unico",
  filter_by_chiamante_edifici: "Filtro chiamante",
  none: "Nessun match",
};

function scoreColor(score: number): string {
  if (score >= 0.9) return "var(--indaco-success)";
  if (score >= 0.7) return "var(--indaco-brand-primary)";
  if (score >= 0.5) return "var(--indaco-warning)";
  return "var(--indaco-danger)";
}

/** Background tint for a confidence score — mirrors scoreColor()'s thresholds,
 *  collapsed onto the three confidence-*-bg tokens (no dedicated "brand" tier). */
function scoreBg(score: number): string {
  if (score >= 0.7) return "var(--confidence-high-bg)";
  if (score >= 0.5) return "var(--confidence-medium-bg)";
  return "var(--confidence-low-bg)";
}

function scoreLabel(score: number): string {
  if (score >= 0.9) return "Molto alta";
  if (score >= 0.7) return "Alta";
  if (score >= 0.5) return "Media";
  if (score >= 0.3) return "Bassa";
  return "Molto bassa";
}

function overallSummary(score: number): string {
  if (score >= 0.9) return "Il sistema è molto sicuro di tutti i match trovati.";
  if (score >= 0.7) return "I match principali sono affidabili, ma è consigliata una verifica rapida.";
  if (score >= 0.5) return "Affidabilità media — alcuni match richiedono attenzione.";
  if (score >= 0.3) return "Affidabilità bassa — il matching ha avuto difficoltà, verifica necessaria.";
  return "Affidabilità molto bassa — la maggior parte dei match è incerta o assente.";
}

interface Props {
  ticket: TicketMatch;
  entityTypes: EntityTypeConfig[];
  keyEntities?: string[];
}

export function ConfidenceExplainer({ ticket, entityTypes, keyEntities = [] }: Props) {
  const [open, setOpen] = useState(false);
  const overall = ticket.overall_confidence;

  // Build per-entity explanations
  const entityExplanations = entityTypes
    .map((et) => {
      const match = ticket.entity_matches[et.key];
      if (!match) return null;

      const confidence = match.confidence ?? match.best?.score ?? 0;
      const method = match.match_method ?? match.best?.method ?? "none";
      const hasBest = !!match.best;
      const nAlts = match.alternatives?.length ?? 0;
      const isKey = keyEntities.includes(et.key);

      // Build detailed explanation
      const parts: string[] = [];
      if (!hasBest) {
        parts.push("Nessun candidato trovato nell'anagrafica.");
        if (method === "none") parts.push("L'email non conteneva dati sufficienti per cercare questa entità.");
      } else {
        parts.push(METHOD_EXPLANATION[method] ?? `Metodo: ${method}.`);
        if (nAlts > 0) {
          parts.push(`Trovati ${nAlts + 1} candidati possibili — il primo è il più probabile (${Math.round(confidence * 100)}%).`);
        }
        if (match.needs_review) {
          parts.push("Lo score è sotto la soglia di auto-approvazione → richiede verifica operatore.");
        }
      }

      return {
        key: et.key,
        label: et.label,
        confidence,
        method,
        hasBest,
        nAlts,
        isKey,
        needsReview: match.needs_review,
        explanation: parts.join(" "),
      };
    })
    .filter(Boolean) as Array<{
      key: string; label: string; confidence: number; method: string;
      hasBest: boolean; nAlts: number; isKey: boolean; needsReview: boolean; explanation: string;
    }>;

  // Problems summary
  const problems: string[] = [];
  const missing = entityExplanations.filter((e) => !e.hasBest);
  const weak = entityExplanations.filter((e) => e.hasBest && e.confidence < 0.5);
  const ambiguous = entityExplanations.filter((e) => e.nAlts >= 3);

  if (missing.length > 0) {
    problems.push(`${missing.map((e) => e.label).join(", ")} non ${missing.length === 1 ? "trovato" : "trovati"}.`);
  }
  if (weak.length > 0) {
    problems.push(`${weak.map((e) => e.label).join(", ")}: score basso.`);
  }
  if (ambiguous.length > 0) {
    problems.push(`${ambiguous.map((e) => e.label).join(", ")}: molti candidati simili.`);
  }

  const keyInfo = keyEntities.length > 0
    ? `La confidence complessiva (${Math.round(overall * 100)}%) si basa su: ${keyEntities.map((k) => entityTypes.find((et) => et.key === k)?.label ?? k).join(", ")}.`
    : `La confidence complessiva (${Math.round(overall * 100)}%) è il minimo tra le entità matchate.`;

  return (
    <div className="card" style={{
      marginBottom: "var(--space-3)",
      borderLeft: `4px solid ${scoreColor(overall)}`,
      fontSize: "var(--font-size-sm)",
    }}>
      {/* Header — always visible */}
      <button
        onClick={() => setOpen(!open)}
        style={{
          background: "none", border: "none", cursor: "pointer", padding: 0,
          display: "flex", alignItems: "center", gap: "var(--space-2)", width: "100%",
          textAlign: "left",
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 18, color: scoreColor(overall) }}>
          query_stats
        </span>
        <span className="fw-semibold" style={{ flex: 1 }}>
          Analisi confidence
        </span>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          padding: "2px 8px", borderRadius: 12,
          background: scoreColor(overall), color: "var(--indaco-white)",
          fontSize: "var(--font-size-xs)", fontWeight: 600,
        }}>
          {Math.round(overall * 100)}% — {scoreLabel(overall)}
        </span>
        <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-gray-400)", transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0)" }}>
          expand_more
        </span>
      </button>

      {/* Summary line */}
      <div className="text-muted" style={{ marginTop: "var(--space-1)", fontSize: "var(--font-size-xs)" }}>
        {overallSummary(overall)}
        {problems.length > 0 && ` ⚠ ${problems.join(" ")}`}
      </div>

      {/* Expandable detail */}
      {open && (
        <div style={{ marginTop: "var(--space-3)", borderTop: "1px solid var(--indaco-gray-200)", paddingTop: "var(--space-3)" }}>
          {/* Strategy info */}
          <div className="text-xs text-muted" style={{ marginBottom: "var(--space-3)" }}>
            {keyInfo}
          </div>

          {/* Per-entity breakdown */}
          {entityExplanations.map((ent) => (
            <div key={ent.key} style={{
              marginBottom: "var(--space-3)", padding: "var(--space-2) var(--space-3)",
              background: "var(--indaco-gray-50)", borderRadius: "var(--border-radius-md)",
              borderLeft: `3px solid ${ent.hasBest ? scoreColor(ent.confidence) : "var(--indaco-gray-300)"}`,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginBottom: 2 }}>
                <span className="fw-semibold">{ent.label}</span>
                {ent.isKey && (
                  <span style={{
                    fontSize: "var(--font-size-xxs)", padding: "1px 6px",
                    background: "var(--indaco-brand-primary)", color: "var(--indaco-white)",
                    borderRadius: 8, fontWeight: 600,
                  }}>CHIAVE</span>
                )}
                {ent.hasBest ? (
                  <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4 }}>
                    <span className="text-xs" style={{
                      padding: "1px 6px", borderRadius: 8,
                      background: scoreBg(ent.confidence),
                      color: scoreColor(ent.confidence), fontWeight: 600,
                    }}>
                      {Math.round(ent.confidence * 100)}%
                    </span>
                    <span className="text-xs text-muted">
                      {METHOD_SHORT[ent.method] ?? ent.method}
                    </span>
                  </span>
                ) : (
                  <span className="text-xs text-muted" style={{ marginLeft: "auto" }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: "middle" }}>warning</span>
                    {" "}Non trovato
                  </span>
                )}
              </div>
              <div className="text-xs text-muted" style={{ lineHeight: 1.5 }}>
                {ent.explanation}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
